<template>
  <div class="redundant-resources">
    <!-- Loading State -->
    <div v-if="!auditResults" class="text-center py-10 text-gray-500">
      <div class="animate-pulse flex flex-col items-center">
        <div class="h-10 w-10 bg-gray-200 rounded-full mb-4"></div>
        <p>Loading redundant resource data...</p>
      </div>
    </div>

    <div v-else>
      <!-- Summary Cards -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div
          v-for="cat in categories"
          :key="cat.id"
          class="summary-card p-4 rounded-xl border text-center cursor-pointer transition-all duration-200"
          :class="
            activeCategory === cat.id
              ? 'bg-indigo-50 border-indigo-300 shadow-md'
              : 'bg-white border-gray-100 hover:border-indigo-200 hover:shadow-sm'
          "
          @click="activeCategory = cat.id"
        >
          <span
            class="block text-2xl font-bold"
            :class="cat.count > 0 ? 'text-red-500' : 'text-green-500'"
            >{{ cat.count }}</span
          >
          <span
            class="text-xs font-medium uppercase tracking-wider text-gray-500 leading-tight"
          >
            {{ cat.label }}
          </span>
        </div>
      </div>

      <!-- Category Detail -->
      <div
        v-for="cat in categories"
        :key="'detail-' + cat.id"
        v-show="activeCategory === cat.id"
        class="detail-panel animate-fadeIn"
      >
        <!-- All clear -->
        <div
          v-if="cat.items.length === 0"
          class="flex flex-col items-center justify-center py-10 text-center text-gray-400"
        >
          <CheckCircle2 class="w-10 h-10 text-green-500 mb-3" />
          <p class="font-medium text-gray-500">
            No redundant {{ cat.label.toLowerCase() }} found.
          </p>
          <p class="text-xs text-gray-400 mt-1">
            This page loads each {{ cat.singularLabel }} only once.
          </p>
        </div>

        <!-- Items List -->
        <div v-else class="space-y-3">
          <div
            v-for="(item, idx) in cat.items"
            :key="idx"
            class="resource-item border rounded-lg overflow-hidden"
          >
            <!-- Item Header -->
            <button
              class="w-full flex items-center justify-between px-4 py-3 bg-red-50 hover:bg-red-100 transition-colors duration-150 text-left"
              @click="toggleItem(cat.id, idx)"
            >
              <div class="flex items-center gap-3 min-w-0">
                <i
                  class="fa-solid fa-triangle-exclamation text-red-500 text-lg flex-shrink-0"
                ></i>
                <div class="min-w-0">
                  <span
                    class="font-mono font-semibold text-gray-800 text-xs break-all"
                    >{{ item.resource }}</span
                  >
                  <span
                    class="ml-2 text-xxs text-gray-400 uppercase tracking-wide"
                    >{{ item.type }}</span
                  >
                </div>
              </div>
              <div class="flex items-center gap-3 flex-shrink-0 ml-4">
                <span
                  class="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full"
                >
                  {{ item.count }}× loaded
                </span>
                <i
                  class="fa-solid text-gray-400 text-xs"
                  :class="
                    expandedItems[cat.id + '-' + idx]
                      ? 'fa-chevron-up'
                      : 'fa-chevron-down'
                  "
                ></i>
              </div>
            </button>

            <!-- Item Locations (expandable) -->
            <div
              v-if="expandedItems[cat.id + '-' + idx]"
              class="px-4 py-3 bg-white border-t divide-y divide-gray-50"
            >
              <div
                v-for="(loc, locIdx) in item.locations"
                :key="locIdx"
                class="py-2 flex items-start gap-2"
              >
                <span
                  class="text-gray-300 font-mono text-xs flex-shrink-0 mt-0.5"
                  >#{{ locIdx + 1 }}</span
                >
                <span class="font-mono text-xs text-blue-700 break-all">{{
                  loc
                }}</span>
              </div>
              <p class="pt-2 text-xxs text-gray-400 flex items-center gap-1">
                <Lightbulb class="w-3 h-3 text-amber-500 flex-shrink-0" />
                <span
                  >Remove duplicate declarations and keep only one reference to
                  this resource.</span
                >
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CheckCircle2, Lightbulb } from "lucide-vue-next";

export default {
  name: "RedundantResources",
  components: {
    CheckCircle2,
    Lightbulb,
  },
  props: {
    auditResults: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      activeCategory: "css",
      expandedItems: {},
    };
  },
  computed: {
    redundant() {
      return (
        this.auditResults?.results?.redundant_resources ?? {
          css: [],
          js: [],
          fonts: [],
          cdn: [],
        }
      );
    },
    categories() {
      return [
        {
          id: "css",
          label: "Redundant CSS",
          singularLabel: "stylesheet",
          count: this.redundant.css?.length ?? 0,
          items: this.redundant.css ?? [],
        },
        {
          id: "js",
          label: "Redundant JS",
          singularLabel: "script",
          count: this.redundant.js?.length ?? 0,
          items: this.redundant.js ?? [],
        },
        {
          id: "fonts",
          label: "Redundant Fonts",
          singularLabel: "font",
          count: this.redundant.fonts?.length ?? 0,
          items: this.redundant.fonts ?? [],
        },
        {
          id: "cdn",
          label: "Redundant CDN",
          singularLabel: "CDN library",
          count: this.redundant.cdn?.length ?? 0,
          items: this.redundant.cdn ?? [],
        },
      ];
    },
    totalRedundant() {
      return (
        (this.redundant.css?.length ?? 0) +
        (this.redundant.js?.length ?? 0) +
        (this.redundant.fonts?.length ?? 0) +
        (this.redundant.cdn?.length ?? 0)
      );
    },
  },
  methods: {
    toggleItem(catId, idx) {
      const key = `${catId}-${idx}`;
      this.expandedItems = {
        ...this.expandedItems,
        [key]: !this.expandedItems[key],
      };
    },
  },
  watch: {
    auditResults: {
      immediate: true,
      handler() {
        this.expandedItems = {};
        // Auto-select first category that has issues
        const order = ["css", "js", "fonts", "cdn"];
        for (const id of order) {
          if ((this.redundant[id]?.length ?? 0) > 0) {
            this.activeCategory = id;
            return;
          }
        }
        this.activeCategory = "css";
      },
    },
  },
};
</script>

<style scoped>
.summary-card {
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.summary-card:hover {
  transform: translateY(-2px);
}

.resource-item {
  transition: box-shadow 0.15s ease;
}
.resource-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
}

.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
